import { useCallback, useEffect, useState } from 'react'

import { Shift, type TeamMember, TeamMemberRole } from '@api'
import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'

import { $account } from '@/models/account.ts'
import type { Creator } from '@/modules/creators/types'

import { getShifts, getShiftsHistory } from '../data/service'
import type { FiltersFormValues } from '../ui/filters'

type ShiftsEntityType = 'shifts' | 'shiftsHistory'

const fetchersByEntityName = {
  shifts: getShifts,
  shiftsHistory: getShiftsHistory,
} as const satisfies Record<
  ShiftsEntityType,
  typeof getShiftsHistory | typeof getShifts
>

export const useGetFilteredShiftsEntities = <
  Entity extends ShiftsEntityType,
  Data extends Awaited<ReturnType<(typeof fetchersByEntityName)[Entity]>>,
>(
  entity: Entity,
  initialFilters: Partial<FiltersFormValues>,
  creators: Creator[] = [],
  teamMembers: TeamMember[] = [],
  isDataLoading: boolean,
) => {
  const [filters, setFilters] = useState<FiltersFormValues>({
    includedCreatorIds: creators.map(({ id }) => String(id)),
    includedMemberIds: teamMembers.map(({ id }) => String(id)),
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
    ...initialFilters,
  })
  const account = useUnit($account)
  const isAdminOrLeader = [TeamMemberRole.ADMIN, TeamMemberRole.LEADER].some(
    (role) => role === account?.memberRole,
  )

  const { from, to, includedCreatorIds, includedMemberIds } = filters

  const { data, isFetching, isIdle } = useQuery<Data>(
    [entity, Object.values({ from, to }).join()],
    () => fetchersByEntityName[entity]({ from, to }) as Data,
    {
      enabled: !isDataLoading,
    },
  )

  useEffect(
    () => {
      if (!isDataLoading) {
        setFilters((prev) => ({
          ...prev,
          includedCreatorIds: creators.map(({ id }) => String(id)),
          includedMemberIds: teamMembers.map(({ id }) => String(id)),
        }))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDataLoading],
  )

  const filterShiftByPeople = useCallback(
    (shift: Shift) =>
      (!isAdminOrLeader ||
        shift.members.some(({ id }) =>
          includedMemberIds.includes(String(id)),
        )) &&
      includedCreatorIds.includes(String(shift.creatorId)),
    [includedCreatorIds, includedMemberIds, isAdminOrLeader],
  )

  const filteredCreators = creators.filter(({ id }) =>
    includedCreatorIds.includes(String(id)),
  )

  return {
    data,
    filteredCreators,
    isLoading: isFetching || isIdle,
    filters,
    setFilters,
    filterShiftByPeople,
  }
}
